import { createSlice, current, PayloadAction } from '@reduxjs/toolkit';
import { userConstats } from '@utils/constants';
import { Permissions } from '@utils/constants/permissions';
import CryptoJS from 'crypto-js';
import { Client } from 'stompjs';

import { LocalStorage } from '../../service/LocalStorage';
import { COLLAPSED_MENU, TOKEN_KEY, USER_KEY } from '../../utils/constants/localStorageKeys';
import { User } from '../../utils/models/User';

export const decryptUserStorage: User | null = (() => {
  const userStorage = localStorage.getItem(USER_KEY);

  if (userStorage) {
    if (userStorage[0] === '{' && userStorage[userStorage.length - 1] === '}') {
      return JSON.parse(userStorage);
    }

    try {
      return userStorage
        ? JSON.parse(CryptoJS.AES.decrypt(userStorage || '', userConstats.CODE_KEY).toString(CryptoJS.enc.Utf8))
        : null;
    } catch (error) {
      localStorage.clear();
      window.location.href = window.location.origin;
      return null;
    }
  }
  return null;
})();

type PType = keyof typeof Permissions;
const arr = Object.entries({ ...Permissions }) as [PType, number][];

interface UserState {
  user: User | null;
  widgets?: User['widgets'];
  firebaseToken?: string | null;
  isPermissions: Record<PType, boolean>;
  version: string;
  stompClient?: Client | null;
  collapse: boolean;
}

const initialState: UserState = {
  user: decryptUserStorage || null,
  widgets: LocalStorage.get('widgets')
    ? (LocalStorage.get('widgets') as User['widgets'])
    : [
        {
          icon: '',
          id: 'files',
          status: true,
          type: 'FILES'
        }
      ],
  firebaseToken: LocalStorage.get<string>('firebaseToken') || null,
  isPermissions: arr?.reduce<Record<PType, boolean>>(
    (p, [k, v]) => {
      if (!Number(k)) {
        p[k] = decryptUserStorage?.role?.permissions?.includes(v);
      }

      return p;
    },
    {} as Record<PType, boolean>
  ),
  version: __APP_VERSION__,
  collapse: !!LocalStorage.get<boolean>(COLLAPSED_MENU)
};

export const userSlice = createSlice({
  name: 'user',
  initialState,
  reducers: {
    setUser: (state, action: PayloadAction<User>) => {
      if (action.payload.roleTokens?.length! > 0) {
        const encrypt = CryptoJS.AES.encrypt(
          JSON.stringify({
            ...action.payload,
            role: action.payload.roleTokens![0].role,
            branchId: action.payload?.roleTokens![0]?.branch?.id
          }),
          userConstats.CODE_KEY
        ).toString();

        state.user = {
          ...action.payload,
          role: action.payload?.roleTokens![0].role,
          branchId: action.payload?.roleTokens![0]?.branch?.id
        };
        LocalStorage.set(USER_KEY, encrypt);
        LocalStorage.set(TOKEN_KEY, action.payload.roleTokens![0].accessToken);
        state.isPermissions = arr?.reduce<Record<PType, boolean>>(
          (p, [k, v]) => {
            if (!Number(k)) {
              p[k] = action.payload.roleTokens?.[0].role?.permissions?.includes(v) || false;
            }

            return p;
          },
          {} as Record<PType, boolean>
        );
      } else {
        const encrypt = CryptoJS.AES.encrypt(JSON.stringify(action.payload), userConstats.CODE_KEY).toString();

        state.user = action.payload;
        LocalStorage.set(USER_KEY, encrypt);
        LocalStorage.set(TOKEN_KEY, action.payload.accessToken);
        state.isPermissions = arr?.reduce<Record<PType, boolean>>(
          (p, [k, v]) => {
            if (!Number(k)) {
              p[k] = action.payload?.role?.permissions?.includes(v) || false;
            }

            return p;
          },
          {} as Record<PType, boolean>
        );
      }
    },
    setUserPayload: (state, action: PayloadAction<User>) => {
      state.user = {
        ...state.user,
        ...action.payload
      };

      const encrypt = CryptoJS.AES.encrypt(
        JSON.stringify({
          ...state.user,
          ...action.payload
        }),
        userConstats.CODE_KEY
      ).toString();

      LocalStorage.set(USER_KEY, encrypt);
    },
    setFirebaseToken: (state, action: PayloadAction<string | null>) => {
      state.firebaseToken = action.payload;
      LocalStorage.set('firebaseToken', action.payload || '');
    },
    clearAvatar: state => {
      const user = { ...current(state.user) };

      if (user) {
        user.avatar = null;

        const encrypt = CryptoJS.AES.encrypt(JSON.stringify(user), userConstats.CODE_KEY).toString();

        LocalStorage.set(USER_KEY, encrypt);

        state.user = user as User;
      }
    },
    addAvatar: (state, action: PayloadAction<string>) => {
      const user = { ...current(state.user) };

      if (user) {
        user.avatar = action.payload;

        const encrypt = CryptoJS.AES.encrypt(JSON.stringify(user), userConstats.CODE_KEY).toString();

        LocalStorage.set(USER_KEY, encrypt);
        state.user = user as User;
      }
    },
    setNewBranch: (state, action: PayloadAction<number>) => {
      const foundBranch = state.user?.roleTokens?.find(el => (el?.branch?.id || el?.branch) === action.payload);

      if (foundBranch) {
        const encrypt = CryptoJS.AES.encrypt(
          JSON.stringify({ ...state.user, role: foundBranch.role, branchId: action.payload }),
          userConstats.CODE_KEY
        ).toString();

        LocalStorage.set(USER_KEY, encrypt);
        LocalStorage.set(TOKEN_KEY, foundBranch.accessToken);
        state.user = { ...state.user, role: foundBranch.role } as User;
      }
    },
    setWidget: (
      state,
      action: PayloadAction<Partial<User['widgets'][number]> & { id: User['widgets'][number]['id'] }>
    ) => {
      const { widgets } = current(state);

      const widgetsCopy = [...(widgets || [])].map(item => {
        if (item.id === action.payload.id) {
          return {
            ...item,
            ...action.payload
          };
        }
        return item;
      });

      state.widgets = widgetsCopy;
      LocalStorage.set('widgets', JSON.stringify(widgetsCopy));
    },
    setVersion: (state, action: PayloadAction<string>) => {
      state.version = action.payload;
    },
    setStompClient: (state, action: PayloadAction<Client | undefined | null>) => {
      state.stompClient = action.payload;
    },
    setCollapse: (state, action: PayloadAction<boolean>) => {
      LocalStorage.set(COLLAPSED_MENU, String(action.payload));
      state.collapse = action.payload;
    }
  }
});

export const {
  setUser,
  clearAvatar,
  addAvatar,
  setNewBranch,
  setWidget,
  setFirebaseToken,
  setUserPayload,
  setVersion,
  setStompClient,
  setCollapse
} = userSlice.actions;
export default userSlice.reducer;
